import React from 'react';
import ReactDOM from 'react-dom';
import App from './components/App';
import * as serviceWorker from './serviceWorker';
import { authStorage, KC_STATUS, keycloak, ROLE_TYPE } from './config';
import { AuthCredential } from 'swuif';
import '@/style/app.less';
import { sbaas_stcms_api } from './config/sbaas';
import { activity } from './components/ActivityMonitoring';
import { extractEmail, isRoleExist } from './config/utils';
import { GlobalContextProvider } from './common/GlobalContext/context';
import { KeycloakTokenParsed } from 'keycloak-js';

if (
  window.location.origin !== 'https://stcms-d.samsungiots.com' &&
  window.location.origin !== 'http://localhost:3000'
) {
  console.log = function() {
    // eslint-disable-next-line
  }.bind(console.log);
  console.info = function() {
    // eslint-disable-next-line
  }.bind(console.info);
  console.warn = function() {
    // eslint-disable-next-line
  }.bind(console.warn);
}

// Helper function to extract department from user info
const extractDepartment = (userInfo: any): string => {
  if (userInfo.email?.includes('@smartthings')) {
    return 'SmartThings';
  }
  if (userInfo.department) {
    const deptArr = userInfo.department.split('/');
    return deptArr[deptArr.length - 1].trim();
  }
  return '';
};

// Helper function to check if user is GSE read-only
const isGSEReadOnly = (userInfo: KeycloakTokenParsed): boolean => {
  return !!(
    userInfo.role &&
    (userInfo.role.gse === ROLE_TYPE.GLOBAL_VIEWER ||
      userInfo.role.gse === ROLE_TYPE.COUNTRY_VIEWER)
  );
};

// Helper function to build user credential object
const buildUserCredential = (userInfo: KeycloakTokenParsed): AuthCredential => {
  const dept = extractDepartment(userInfo);

  return {
    key: userInfo['preferred_username'] || '',
    user: {
      username: userInfo['preferred_username'] || '',
      userId: userInfo.sub || '',
      fullName: userInfo['name'] || userInfo['given_name'] || '',
      firstName: userInfo['given_name'] || '',
      lastName: userInfo['family_name'] || '',
      email: extractEmail(userInfo['email']) || '',
      roles: userInfo.role || {},
      operation: userInfo.role?.operation || false,
      department: dept,
      originCountry: userInfo.origin_country || '',
      registration_status: userInfo['registration_status'] || '',
      countries: userInfo.countries || {},
      authTime: userInfo.auth_time || {},
      gseReadOnly: isGSEReadOnly(userInfo),
      gseVendor: userInfo.gseVendor || undefined,
    },
  };
};

// Helper function to determine app status based on user info
const determineAppStatus = (userInfo: KeycloakTokenParsed): string => {
  const isActiveOrApproved =
    userInfo['registration_status'] &&
    (userInfo['registration_status'] === KC_STATUS.ACTIVE ||
      userInfo['registration_status'] === KC_STATUS.APPROVED);

  if (isActiveOrApproved && isRoleExist(userInfo.role)) {
    return 'main';
  }
  return 'login';
};

// Helper function to update last login
const updateLastLogin = (username: string): void => {
  sbaas_stcms_api.script('update-last-login').put({ query: { username } });
};

// Helper function to handle authenticated user
const handleAuthenticatedUser = async (
  userInfo: KeycloakTokenParsed,
): Promise<string> => {
  const userCredential = buildUserCredential(userInfo);
  const status = determineAppStatus(userInfo);

  updateLastLogin(userInfo['preferred_username']);
  await authStorage.store(userCredential, true);
  activity.setUser(
    userCredential.user,
    window.screen.availHeight,
    window.screen.availWidth,
  );

  return status;
};

// Helper function to handle unauthenticated user
const handleUnauthenticatedUser = async (): Promise<string> => {
  if (window.location.pathname.includes('/user/edit')) {
    localStorage.setItem('userApproveLink', window.location.pathname);
  }

  await authStorage.store(undefined, false);
  activity.setUser();

  return 'login';
};

// Helper function to render the app
const renderApp = (status: string): void => {
  ReactDOM.render(
    <GlobalContextProvider>
      <App status={status} />
    </GlobalContextProvider>,
    document.getElementById('root'),
  );
};

// Main initialization
keycloak.instance
  .init({
    onLoad: 'check-sso',
    checkLoginIframe: false,
  })
  .then(async (authenticated: any) => {
    const userInfo: KeycloakTokenParsed | undefined =
      keycloak.instance.tokenParsed;
    const status =
      authenticated && userInfo
        ? await handleAuthenticatedUser(userInfo)
        : await handleUnauthenticatedUser();

    renderApp(status);
  });

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: http://bit.ly/CRA-PWA
serviceWorker.unregister();
